/**
 * src/services/busBookingAPI.js
 *
 * All API calls for the RouteChef bus booking flow.
 */

import { FEATURES } from "../config/features";

/**
 * Resolve bus_test flag: prefer value embedded in the result card/segment
 * (set by route search backend), fall back to FEATURES.BUS_TEST.
 * Pass cardOrSegment = the busInfo / segment object from result card.
 */
export function resolveBusTest(cardOrSegment) {
    if (cardOrSegment && typeof cardOrSegment.bus_test === 'boolean') {
        return cardOrSegment.bus_test;
    }
    if (cardOrSegment?.segment && typeof cardOrSegment.segment.bus_test === 'boolean') {
        return cardOrSegment.segment.bus_test;
    }
    return FEATURES.BUS_TEST ?? true;
}

export const BUS_TEST = FEATURES.BUS_TEST ?? false;
const ORCHESTRATOR_URL = BUS_TEST
    ? "https://s4nvnzwoc8.execute-api.ap-south-1.amazonaws.com/default/tbo_api_orchesterator/development"
    : "https://s4nvnzwoc8.execute-api.ap-south-1.amazonaws.com/default/tbo_api_orchesterator";


// ─────────────────────────────────────────────────────────────────────────────
// Persistent anonymous identity
// ─────────────────────────────────────────────────────────────────────────────

const ANON_USER_STORAGE_KEY =
    "routechef_anonymous_user_id";


function getOrCreateAnonymousId() {
    try {
        let anonymousId =
            localStorage.getItem(
                ANON_USER_STORAGE_KEY
            );

        if (
            anonymousId &&
            String(anonymousId).trim()
        ) {
            return String(
                anonymousId
            ).trim();
        }


        if (
            typeof crypto !== "undefined" &&
            typeof crypto.randomUUID === "function"
        ) {
            anonymousId =
                "anon" +
                crypto
                    .randomUUID()
                    .replace(/-/g, "");
        } else {
            anonymousId =
                "anon" +
                Date.now().toString(36) +
                Math.random()
                    .toString(36)
                    .slice(2, 14);
        }


        localStorage.setItem(
            ANON_USER_STORAGE_KEY,
            anonymousId
        );

        return anonymousId;

    } catch (error) {

        console.error(
            "[busBookingAPI] Failed to create anonymous ID:",
            error
        );

        return (
            "anon" +
            Date.now().toString(36) +
            Math.random()
                .toString(36)
                .slice(2, 14)
        );
    }
}


// ─────────────────────────────────────────────────────────────────────────────
// Resolve RouteChef identity
// ─────────────────────────────────────────────────────────────────────────────

function resolveUserIdentifier(userContact) {
    const normalized =
        String(
            userContact || ""
        ).trim();

    if (
        normalized &&
        normalized.toLowerCase() !== "anon"
    ) {
        return normalized;
    }

    return getOrCreateAnonymousId();
}


// ─────────────────────────────────────────────────────────────────────────────
// Generic POST helper
// ─────────────────────────────────────────────────────────────────────────────

async function orchestratorPost(
    payload,
    userToken = null,
    userContact = null
) {
    const resolvedUserId =
        resolveUserIdentifier(
            userContact
        );


    const requestBody = {
        ...payload,

        From:
            `webbot_new:${resolvedUserId}`,

        headers:
            userToken
                ? {
                    Authorization:
                        userToken,
                }
                : {},

        bus_test:
            Boolean(
                FEATURES.BUS_TEST
            ),
    };


    console.log(
        "[busBookingAPI] Request:",
        {
            intent:
                requestBody.intent,

            rc_booking_id:
                requestBody.rc_booking_id,

            From:
                requestBody.From,

            bus_test:
                requestBody.bus_test,
        }
    );


    const response =
        await fetch(
            ORCHESTRATOR_URL,
            {
                method: "POST",

                headers: {
                    "Content-Type":
                        "application/json",
                },

                body:
                    JSON.stringify(
                        requestBody
                    ),
            }
        );


    let json;

    try {

        json =
            await response.json();

    } catch (error) {

        throw new Error(
            `Invalid API response (${response.status})`
        );
    }


    if (
        json?.body &&
        typeof json.body === "string"
    ) {

        try {

            return JSON.parse(
                json.body
            );

        } catch (error) {

            console.error(
                "[busBookingAPI] Failed parsing Lambda body:",
                error
            );
        }
    }


    return json;
}


// ─────────────────────────────────────────────────────────────────────────────
// 1. Bus seat layout
// ─────────────────────────────────────────────────────────────────────────────

export async function getBusSeatLayout({
    sourceCity,
    destinationCity,
    inventoryType,
    routeScheduleId,
    date,
    userToken,
    userContact,
    rcBookingId,
}) {
    return orchestratorPost(
        {
            intent:
                "bus_seat_layout",

            sourceCity,

            destinationCity,

            inventoryType:
                String(
                    inventoryType || ""
                ),

            routeScheduleId,

            date,

            rc_booking_id:
                rcBookingId,
        },

        userToken,

        userContact
    );
}


// ─────────────────────────────────────────────────────────────────────────────
// 2. Get bus promo codes
// ─────────────────────────────────────────────────────────────────────────────

export async function getBusPromoCodes({
    rcBookingId,
    userToken,
    userContact,
}) {
    return orchestratorPost(
        {
            intent:
                "get_bus_promo_codes",

            rc_booking_id:
                rcBookingId,
        },

        userToken,

        userContact
    );
}


// ─────────────────────────────────────────────────────────────────────────────
// 3. Verify bus promo code
// ─────────────────────────────────────────────────────────────────────────────
//
// `amount` should be the promo amount expected by your backend contract.
//

export async function verifyBusPromoCode({
    rcBookingId,
    promoCode,
    amount,
    userToken,
    userContact,
}) {
    return orchestratorPost(
        {
            intent:
                "verify_bus_promo",

            rc_booking_id:
                rcBookingId,

            promo_code:
                String(
                    promoCode || ""
                )
                    .trim()
                    .toUpperCase(),

            amount:
                String(
                    amount ?? 0
                ),
        },

        userToken,

        userContact
    );
}


// ─────────────────────────────────────────────────────────────────────────────
// 4. Create Razorpay order
// ─────────────────────────────────────────────────────────────────────────────

export async function createBusOrder({
    rcBookingId,
    totalFare,
    appliedPromoCode,
    appliedPromoDiscount,
    blockPayload,
    segmentInfo,
    busTest,
    userToken,
    userContact,
}) {
    return orchestratorPost(
        {
            intent:
                "create_order_bus",

            rc_booking_id:
                rcBookingId,

            amount:
                Number(
                    totalFare || 0
                ),

            applied_promo_code:
                appliedPromoCode
                    ? String(
                        appliedPromoCode
                    )
                        .trim()
                        .toUpperCase()
                    : null,

            applied_promo_discount:
                Number(
                    appliedPromoDiscount || 0
                ),

            block_payload:
                blockPayload,

            segment_info:
                segmentInfo || null,

            bus_test:
                typeof busTest === 'boolean' ? busTest : (FEATURES.BUS_TEST ?? true),
        },

        userToken,

        userContact
    );
}


// ─────────────────────────────────────────────────────────────────────────────
// 5. Update bus booking status
// ─────────────────────────────────────────────────────────────────────────────

export async function updateBusBookingStatus({
    rcBookingId,
    newStatus,
    paymentId,
    paymentStatus,
    paymentMeta,
    userToken,
    userContact,
}) {
    return orchestratorPost(
        {
            intent:
                "updatebookingstatus",

            rc_booking_id:
                rcBookingId,

            new_status:
                newStatus,

            payment_id:
                paymentId,

            payment_status:
                paymentStatus,

            payment_meta:
                paymentMeta,

            booking_type:
                "bus",

            bus_test:
                FEATURES.BUS_TEST ?? true,
        },

        userToken,

        userContact
    );
}


// ─────────────────────────────────────────────────────────────────────────────
// 6. Poll booking status
// ─────────────────────────────────────────────────────────────────────────────
//
// Existing status endpoint.
//
// For migrated anonymous bookings, backend should use rc_booking_id_index
// rather than requiring the anonymous user_id.
//

export async function getBusBookingStatus({
    rcBookingId,
    userToken = null,
    userContact = null,
}) {
    return orchestratorPost(
        {
            intent:
                "get_bus_status",

            rc_booking_id:
                rcBookingId,
        },

        userToken,

        userContact
    );
}


// ─────────────────────────────────────────────────────────────────────────────
// 7. Get booking using rc_booking_id alone
// ─────────────────────────────────────────────────────────────────────────────
//
// Used when:
//
// /book-bus/status?rc_booking_id=RCFB...
//
// is refreshed.
//
// Backend must query rc_booking_id_index.
//

export async function getBusBookingById({
    rcBookingId,
    userToken = null,
    userContact = null,
}) {
    return orchestratorPost(
        {
            intent:
                "get_bus_booking_by_id",

            rc_booking_id:
                rcBookingId,
        },

        userToken,

        userContact
    );
}


// ─────────────────────────────────────────────────────────────────────────────
// Razorpay checkout
// ─────────────────────────────────────────────────────────────────────────────

export function openBusPaymentModal({
    rzpOrderId,
    rzpKeyId,
    amountRupees,
    rcBookingId,
    userContact,
    userName,
    onSuccess,
    onFailure,
    onDismiss,
}) {
    const loadAndOpen = () => {

        if (!window.Razorpay) {

            onFailure?.(
                "Payment gateway failed to load"
            );

            return;
        }


        const options = {

            key:
                rzpKeyId,

            amount:
                Math.round(
                    Number(
                        amountRupees || 0
                    ) * 100
                ),

            currency:
                "INR",

            name:
                "RouteChef",

            description:
                `Bus Booking – ${rcBookingId}`,

            order_id:
                rzpOrderId,


            prefill: {

                name:
                    userName || "",

                email:
                    userContact?.includes("@")
                        ? userContact
                        : "",

                contact:
                    userContact &&
                    !userContact.includes("@")
                        ? String(
                            userContact
                        ).replace(
                            /^\+91/,
                            ""
                        )
                        : "",
            },


            theme: {
                color:
                    "#FB5607",
            },


            handler:
                (response) => {

                    onSuccess?.(
                        response
                    );
                },


            modal: {

                ondismiss:
                    () => {

                        onDismiss?.();
                    },
            },
        };


        const razorpay =
            new window.Razorpay(
                options
            );


        razorpay.on(
            "payment.failed",

            (response) => {

                const message =
                    response?.error?.description ||
                    "Payment failed";

                onFailure?.(
                    message
                );
            }
        );


        razorpay.open();
    };


    if (window.Razorpay) {

        loadAndOpen();

        return;
    }


    const existingScript =
        document.querySelector(
            'script[src="https://checkout.razorpay.com/v1/checkout.js"]'
        );


    if (existingScript) {

        existingScript.addEventListener(
            "load",
            loadAndOpen,
            {
                once: true,
            }
        );


        existingScript.addEventListener(
            "error",

            () => {
                onFailure?.(
                    "Failed to load payment gateway"
                );
            },

            {
                once: true,
            }
        );


        return;
    }


    const script =
        document.createElement(
            "script"
        );


    script.src =
        "https://checkout.razorpay.com/v1/checkout.js";

    script.async =
        true;

    script.onload =
        loadAndOpen;

    script.onerror =
        () => {

            onFailure?.(
                "Failed to load payment gateway"
            );
        };


    document.body.appendChild(
        script
    );
}
